import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { Copy, MagnifyingGlass } from "@phosphor-icons/react";

export default function Scripts() {
  const [scripts, setScripts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/scripts")
      .then((r) => setScripts(r.data || []))
      .catch(() => toast.error("Failed to load scripts"))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(scripts.map((s) => s.category)))];

  const filtered = scripts.filter(
    (s) =>
      (filter === "All" || s.category === filter) &&
      (search === "" ||
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        s.body.toLowerCase().includes(search.toLowerCase()))
  );

  const copy = (text, title) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied: ${title}`);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        <p className="eyebrow"><span className="gold-divider" />Playbook</p>
        <h1 className="font-display text-4xl mt-3">Script Library</h1>
        <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
          Cold DMs, qualifying questions, IUL/Annuity pitches, objection handlers, closes, follow-ups. Click any card to copy.
        </p>

        {/* Search + filters */}
        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-exodus-muted" />
            <input
              data-testid="script-search"
              className="input-exodus pl-10"
              placeholder="Search scripts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-xs px-4 py-2 border transition-colors ${
                  filter === c
                    ? "border-exodus-gold text-exodus-gold"
                    : "border-exodus-border text-exodus-muted hover:text-exodus-text"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <p className="text-exodus-muted mt-10">Loading…</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            {filtered.map((s) => (
              <div
                key={s.id}
                data-testid={`script-card-${s.id}`}
                className="card-surface p-7 hover:border-exodus-gold transition-colors group cursor-pointer"
                onClick={() => copy(s.body, s.title)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="eyebrow text-xs">{s.category}</p>
                    <h3 className="font-display text-xl mt-2">{s.title}</h3>
                  </div>
                  <Copy
                    size={16}
                    className="text-exodus-muted group-hover:text-exodus-gold transition-colors shrink-0 mt-1"
                  />
                </div>
                <pre className="text-sm text-exodus-text/80 mt-4 leading-relaxed whitespace-pre-wrap font-sans">
                  {s.body}
                </pre>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-2 text-exodus-muted text-center py-12">No scripts match your search.</p>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
